import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import CheckCircleIcon from '../../components/icons/CheckCircleIcon';
import CloseCircleIcon from '../../components/icons/CloseCircleIcon';
import SeeIcon from '../../components/icons/SeeIcon';
import CloseIcon from '../../components/icons/CloseIcon';
import PlusIcon from '../../components/icons/PlusIcon';

interface SchoolRequest {
  id: string;
  schoolName: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  message?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
  requestType: 'teacher' | 'student';
}

const SchoolRequestManagement: React.FC = () => {
  const [requests, setRequests] = useState<SchoolRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<SchoolRequest | null>(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [openSchoolDialog, setOpenSchoolDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [schoolForm, setSchoolForm] = useState({
    name: '',
    code: '',
    address: '',
    phone: ''
  });

  // 요청 목록 가져오기
  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const requestsRef = collection(db, 'schoolRequests');
      const q = query(requestsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const requestsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as SchoolRequest[];
      
      setRequests(requestsList);
    } catch (error) {
      console.error('요청 목록 가져오기 오류:', error);
      setError('요청 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 요청 승인
  const handleApprove = async (request: SchoolRequest) => {
    try {
      setProcessing(true);
      
      // 요청 상태 업데이트
      await updateDoc(doc(db, 'schoolRequests', request.id), {
        status: 'approved',
        approvedAt: new Date()
      });

      // 학교 등록 다이얼로그 열기
      setSelectedRequest(request);
      setSchoolForm({
        name: request.schoolName,
        code: '',
        address: '',
        phone: request.contactPhone || ''
      });
      setOpenSchoolDialog(true);
      
      await fetchRequests();
    } catch (error) {
      console.error('요청 승인 오류:', error);
      setError('요청 승인 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  // 요청 거절
  const handleReject = async (requestId: string) => {
    if (!window.confirm('정말로 이 요청을 거절하시겠습니까?')) {
      return;
    }

    try {
      setProcessing(true);
      
      await updateDoc(doc(db, 'schoolRequests', requestId), {
        status: 'rejected',
        rejectedAt: new Date()
      });
      
      await fetchRequests();
    } catch (error) {
      console.error('요청 거절 오류:', error);
      setError('요청 거절 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  // 학교 등록
  const handleRegisterSchool = async () => {
    if (!schoolForm.name || !schoolForm.code) {
      setError('학교명과 학교코드는 필수입니다.');
      return;
    }

    try {
      setProcessing(true);
      
      // 학교 등록
      await addDoc(collection(db, 'schools'), {
        name: schoolForm.name,
        code: schoolForm.code,
        address: schoolForm.address,
        phone: schoolForm.phone,
        createdAt: new Date(),
        status: 'active'
      });

      setOpenSchoolDialog(false);
      setSchoolForm({ name: '', code: '', address: '', phone: '' });
      await fetchRequests();
    } catch (error) {
      console.error('학교 등록 오류:', error);
      setError('학교 등록 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 12px',
            borderRadius: '12px',
            backgroundColor: '#FFF7E6',
            color: '#D69E2E',
            fontSize: '12px',
            fontWeight: 500
          }}>
            대기중
          </div>
        );
      case 'approved':
        return (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 12px',
            borderRadius: '12px',
            backgroundColor: '#F0FFF4',
            color: '#38A169',
            fontSize: '12px',
            fontWeight: 500
          }}>
            <CheckCircleIcon width={14} height={14} color="#38A169" />
            승인됨
          </div>
        );
      case 'rejected':
        return (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 12px',
            borderRadius: '12px',
            backgroundColor: '#FFF5F5',
            color: '#FF3838',
            fontSize: '12px',
            fontWeight: 500
          }}>
            <CloseCircleIcon width={14} height={14} color="#FF3838" />
            거절됨
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px' 
      }}>
        <div style={{ fontSize: '16px', color: 'var(--Color-text-normal, #161616)' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* 헤더 */}
      <div
        style={{
          fontSize: '24px',
          fontWeight: 600,
          lineHeight: '36px',
          color: 'var(--Color-text-strong, #0D0D0D)'
        }}
      >
        학교 등록 요청 관리
      </div>

      {/* 에러 메시지 */}
      {error && (
        <Card
          padding="12px 16px"
          background="#FFF5F5"
          style={{ border: '1px solid #FFE0E0' }}
        >
          <div style={{ fontSize: '14px', color: '#FF3838' }}>{error}</div>
        </Card>
      )}

      {/* 학교 목록 테이블 */}
      {requests.length === 0 ? (
        <Card padding="40px" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: 'var(--Color-text-normal, #161616)' }}>
            등록된 요청이 없습니다.
          </div>
        </Card>
      ) : (
        <Card padding="0" style={{ overflow: 'hidden', width: '100%' }}>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--Color-fill-hover, #F5F5F5)' }}>
                  <th style={{
                    padding: '12px 20px',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    whiteSpace: 'nowrap'
                  }}>
                    학교명
                  </th>
                  <th style={{
                    padding: '12px 20px',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    whiteSpace: 'nowrap'
                  }}>
                    담당자
                  </th>
                  <th style={{
                    padding: '12px 20px',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    minWidth: '200px'
                  }}>
                    연락처
                  </th>
                  <th style={{
                    padding: '12px 20px',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    whiteSpace: 'nowrap'
                  }}>
                    요청일
                  </th>
                  <th style={{
                    padding: '12px 20px',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    whiteSpace: 'nowrap'
                  }}>
                    상태
                  </th>
                  <th style={{
                    padding: '12px 20px',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    whiteSpace: 'nowrap'
                  }}>
                    작업
                  </th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id} style={{
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                  }}>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {request.schoolName}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {request.contactName}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      minWidth: '200px'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div>{request.contactEmail}</div>
                        {request.contactPhone && (
                          <div style={{ fontSize: '12px', color: 'var(--Color-text-normal, #161616)' }}>
                            {request.contactPhone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {request.createdAt.toLocaleDateString('ko-KR')}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {getStatusBadge(request.status)}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      textAlign: 'center',
                      whiteSpace: 'nowrap'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedRequest(request);
                            setOpenDetailDialog(true);
                          }}
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--Color-fill-hover, #F5F5F5)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="상세보기"
                        >
                          <SeeIcon width={16} height={16} />
                        </button>
                        
                        {request.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleApprove(request)}
                              disabled={processing}
                              style={{
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none',
                                backgroundColor: 'transparent',
                                cursor: processing ? 'not-allowed' : 'pointer',
                                borderRadius: '6px',
                                transition: 'background-color 0.2s ease',
                                opacity: processing ? 0.5 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (!processing) {
                                  e.currentTarget.style.backgroundColor = '#F0FFF4';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                              title="승인"
                            >
                              <CheckCircleIcon width={16} height={16} color="#38A169" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleReject(request.id)}
                              disabled={processing}
                              style={{
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none',
                                backgroundColor: 'transparent',
                                cursor: processing ? 'not-allowed' : 'pointer',
                                borderRadius: '6px',
                                transition: 'background-color 0.2s ease',
                                opacity: processing ? 0.5 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (!processing) {
                                  e.currentTarget.style.backgroundColor = '#FFF5F5';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                              title="거절"
                            >
                              <CloseCircleIcon width={16} height={16} color="#FF3838" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* 상세보기 다이얼로그 */}
      {openDetailDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            fontFamily: 'Pretendard, sans-serif'
          }}
          onClick={() => setOpenDetailDialog(false)}
        >
          <div
            style={{
              width: '90%',
              maxWidth: '500px',
              padding: '0',
              borderRadius: '16px',
              background: 'var(--Color-background-normal, white)',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
              position: 'relative',
              overflow: 'hidden',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            {/* 다이얼로그 헤더 */}
            <div style={{
              padding: '32px',
              borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{
                fontSize: '20px',
                fontWeight: 600,
                color: 'var(--Color-text-strong, #0D0D0D)'
              }}>
                요청 상세정보
              </div>
              <button
                type="button"
                onClick={() => setOpenDetailDialog(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--Color-fill-hover, #F5F5F5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <CloseIcon width={20} height={20} />
              </button>
            </div>

            {/* 다이얼로그 내용 */}
            {selectedRequest && (
              <div style={{
                padding: '32px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px'
              }}>
                <div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'var(--Color-text-normal, #161616)',
                    marginBottom: '8px'
                  }}>
                    학교명
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--Color-text-strong, #0D0D0D)'
                  }}>
                    {selectedRequest.schoolName}
                  </div>
                </div>

                <div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'var(--Color-text-normal, #161616)',
                    marginBottom: '8px'
                  }}>
                    담당자명
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--Color-text-strong, #0D0D0D)'
                  }}>
                    {selectedRequest.contactName}
                  </div>
                </div>

                <div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'var(--Color-text-normal, #161616)',
                    marginBottom: '8px'
                  }}>
                    연락처 이메일
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--Color-text-strong, #0D0D0D)'
                  }}>
                    {selectedRequest.contactEmail}
                  </div>
                </div>

                {selectedRequest.contactPhone && (
                  <div>
                    <div style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'var(--Color-text-normal, #161616)',
                      marginBottom: '8px'
                    }}>
                      연락처 전화번호
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: 'var(--Color-text-strong, #0D0D0D)'
                    }}>
                      {selectedRequest.contactPhone}
                    </div>
                  </div>
                )}

                {selectedRequest.message && (
                  <div>
                    <div style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'var(--Color-text-normal, #161616)',
                      marginBottom: '8px'
                    }}>
                      추가 메시지
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: 'var(--Color-text-strong, #0D0D0D)',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {selectedRequest.message}
                    </div>
                  </div>
                )}

                <div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'var(--Color-text-normal, #161616)',
                    marginBottom: '8px'
                  }}>
                    요청일
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--Color-text-strong, #0D0D0D)'
                  }}>
                    {selectedRequest.createdAt.toLocaleString('ko-KR')}
                  </div>
                </div>
              </div>
            )}

            {/* 다이얼로그 푸터 */}
            <div style={{
              padding: '24px 32px',
              borderTop: '1px solid var(--Color-line-normal, #F0F0F0)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <Button
                variant="cancel"
                onClick={() => setOpenDetailDialog(false)}
              >
                닫기
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 학교 등록 다이얼로그 */}
      {openSchoolDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            fontFamily: 'Pretendard, sans-serif'
          }}
          onClick={() => setOpenSchoolDialog(false)}
        >
          <div
            style={{
              width: '90%',
              maxWidth: '600px',
              padding: '0',
              borderRadius: '16px',
              background: 'var(--Color-background-normal, white)',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
              position: 'relative',
              overflow: 'hidden',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            {/* 다이얼로그 헤더 */}
            <div style={{
              padding: '32px',
              borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{
                fontSize: '20px',
                fontWeight: 600,
                color: 'var(--Color-text-strong, #0D0D0D)'
              }}>
                학교 등록
              </div>
              <button
                type="button"
                onClick={() => setOpenSchoolDialog(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--Color-fill-hover, #F5F5F5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <CloseIcon width={20} height={20} />
              </button>
            </div>

            {/* 다이얼로그 내용 */}
            <div style={{
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              <InputField
                label="학교명"
                value={schoolForm.name}
                onChange={(value) => setSchoolForm(prev => ({ ...prev, name: value }))}
                required
                width="100%"
              />

              <div>
                <InputField
                  label="학교코드"
                  value={schoolForm.code}
                  onChange={(value) => setSchoolForm(prev => ({ ...prev, code: value }))}
                  required
                  width="100%"
                />
                <div style={{
                  fontSize: '12px',
                  color: 'var(--Color-text-normal, #161616)',
                  marginTop: '4px'
                }}>
                  6자리 숫자로 입력해주세요
                </div>
              </div>

              <InputField
                label="주소"
                value={schoolForm.address}
                onChange={(value) => setSchoolForm(prev => ({ ...prev, address: value }))}
                width="100%"
              />

              <InputField
                label="전화번호"
                value={schoolForm.phone}
                onChange={(value) => setSchoolForm(prev => ({ ...prev, phone: value }))}
                width="100%"
              />
            </div>

            {/* 다이얼로그 푸터 */}
            <div style={{
              padding: '24px 32px',
              borderTop: '1px solid var(--Color-line-normal, #F0F0F0)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <Button
                variant="cancel"
                onClick={() => setOpenSchoolDialog(false)}
                disabled={processing}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={handleRegisterSchool}
                disabled={processing}
                leftIcon={processing ? undefined : <PlusIcon width={16} height={16} fill="white" />}
              >
                {processing ? '처리 중...' : '학교 등록'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolRequestManagement;
